import { useTranslation } from 'react-i18next'
import { CheckIcon, CopyIcon, XIcon } from 'lucide-react'
import {
	Button,
	Dialog, DialogClose, DialogContent, DialogTitle, DialogDescription,
} from '@rentflowdev/ui'

interface InviteSentDialogProps {
	open:          boolean
	onOpenChange:  (open: boolean) => void
	fullName?:     string
	email?:        string
	inviteLink?:   string
}

function initialsBrief(name?: string): string {
	if (!name) return ''
	const parts = name.trim().split(/\s+/).slice(0, 3)
	return parts.map((p) => `${p[0]?.toUpperCase() ?? ''}.`).join('')
}

export function InviteSentDialog({ open, onOpenChange, fullName, email, inviteLink }: InviteSentDialogProps) {
	const { t } = useTranslation()

	async function handleCopyLink() {
		if (!inviteLink) return
		try {
			await navigator.clipboard.writeText(inviteLink)
		} catch (err) {
			console.error('clipboard write failed:', err)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col items-center gap-0 p-0 rounded-(--radius-lg)"
				style={{ width: 440, maxWidth: '95vw' }}
			>
				<DialogClose
					render={
						<button
							type="button"
							className="absolute right-4 top-4 rounded-(--radius-sm) p-0.5 text-(--color-muted-foreground) transition-colors hover:text-(--color-foreground)"
						/>
					}
				>
					<XIcon className="size-5" />
				</DialogClose>

				<div className="flex w-full flex-col items-center px-8 py-10">
					<div className="relative mb-6 flex size-24 items-center justify-center rounded-full bg-gradient-to-br from-(--color-primary)/20 to-(--color-info)/20">
						<div className="flex size-12 items-center justify-center rounded-full bg-(--color-primary)">
							<CheckIcon className="size-6 text-(--color-primary-foreground)" />
						</div>
					</div>

					<DialogTitle className="mb-2 text-center text-xl font-[500] leading-7 tracking-[-0.4px] text-(--color-foreground)">
						{t('settings.team.inviteSent.title')}
					</DialogTitle>
					<DialogDescription className="mb-6 text-center text-sm text-(--color-muted-foreground)">
						{t('settings.team.inviteSent.description', {
							name:  initialsBrief(fullName),
							email: email ?? '',
						})}
					</DialogDescription>

					{inviteLink && (
						<Button
							type="button"
							onClick={() => void handleCopyLink()}
							iconLeft={<CopyIcon className="size-4" />}
						>
							{t('settings.team.inviteSent.copyLink')}
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
